/**
 * Labstep
 *
 * @module screens/ProtocolCollection/Edit/Right/Steps
 * @desc Protocol Elements Steps
 */

import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ProtocolStepActionStartEnd } from 'labstep-web/components/ProtocolStep/Action/StartEnd';
import { ProtocolStepList } from 'labstep-web/components/ProtocolStep/List';
import { ICONS } from 'labstep-web/constants/icons';
import { CardEmptyState } from 'labstep-web/core/Card/EmptyState';
import { Icon } from 'labstep-web/core/Icon';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import type React from 'react';
import styles from './styles.module.scss';
import type { IProtocolStepManagerProps } from './types';

export const ProtocolStepManager: React.FC<
  IProtocolStepManagerProps
> = ({ protocol, protocolCollection }) => {
  const canEdit = useHasAccess(
    protocol.entityName,
    protocol.id,
    Action.edit,
  );
  return (
    <div>
      {protocol.protocolStepsOrdered.length === 0 && (
        <CardEmptyState
          src="/img/empty-state/protocols.svg"
          explanation={
            <div>
              Use the <Icon name={ICONS.protocol.step} /> button
              {` to add steps to your ${getHumanReadableEntityName(
                ProtocolCollection.entityName,
              )} so you track which actions have been completed${
                protocol instanceof Experiment
                  ? ''
                  : ` each time you follow the ${getHumanReadableEntityName(
                      ProtocolCollection.entityName,
                    )}`
              }.`}
            </div>
          }
        />
      )}
      {protocol instanceof Experiment &&
        protocol.protocolStepsOrdered.length > 0 &&
        canEdit &&
        !protocol.isTemplate && (
          <div className={styles.completeAction}>
            {!protocol.hasAllStepsCompleted && (
              <span className={styles.completeOption}>
                <ProtocolStepActionStartEnd
                  actionComponentProps={{
                    type: 'text',
                    text: 'Mark all as complete',
                    disabled: !canEdit,
                  }}
                  protocolSteps={protocol.protocolStepsOrdered.filter(
                    (step) => !step.ended_at,
                  )}
                />
              </span>
            )}
            {protocol.hasAtLeastOneStepCompleted && (
              <span className={styles.completeOption}>
                <ProtocolStepActionStartEnd
                  actionComponentProps={{
                    text: 'Mark all as incomplete',
                    type: 'text',
                    disabled: !canEdit,
                  }}
                  endedAt={null}
                  protocolSteps={protocol.protocolStepsOrdered}
                />
              </span>
            )}
          </div>
        )}
      <div className={styles.listContainer}>
        <ProtocolStepList
          protocol={protocol}
          protocolSteps={protocol.protocolStepsOrdered}
          protocolCollection={protocolCollection}
        />
      </div>
    </div>
  );
};
